import { type ReactNode, useEffect } from "react";
import {
  BOOKING_MAIL,
  KONTAKT_MAIL,
  LEGAL_ADDRESS,
  LEGAL_PHONE,
  PRAXIS_ADDRESSES,
} from "./legalContent";
import { publicSiteIdentity } from "./lib/bookingHost";
import { SiteFooter } from "./SiteFooter";
import "./legal.css";

export function ImpressumPage() {
  return (
    <LegalShell title="Impressum" kicker="Angaben nach DDG" heading="Wer hier verantwortlich ist.">
      <p className="legal-page__lead">
        Eine Buchungsseite, drei Leistungsbereiche. Damit klar ist, mit wem du es zu tun hast.
      </p>

      <section>
        <h2>Anbieter dieser Website</h2>
        <p>
          Diese Buchungsseite unter <strong>book.raykschoerling.com</strong> wird betrieben von:
        </p>
        <PersonBlock
          name="Rayk Schörling"
          lines={[
            LEGAL_ADDRESS.street,
            `${LEGAL_ADDRESS.zip} ${LEGAL_ADDRESS.city}`,
            `Telefon: ${LEGAL_PHONE}`,
            KONTAKT_MAIL,
            `Buchung: ${BOOKING_MAIL}`,
          ]}
        />
        <p>
          Verantwortlich für den Inhalt nach §&nbsp;18 Abs.&nbsp;2 MStV: Rayk Schörling,{" "}
          {LEGAL_ADDRESS.street}, {LEGAL_ADDRESS.zip} {LEGAL_ADDRESS.city}.
        </p>
      </section>

      <section>
        <h2>Vertragspartner</h2>
        <p>
          Je nach Buchung kommt der Vertrag mit einem anderen Haus zustande. Das steht in der
          Buchungsbestätigung und auf der Rechnung.
        </p>

        <h3>Coaching</h3>
        <p>Einzeltermine, Kennenlernen und persönliches Gespräch.</p>
        <PersonBlock
          name="Rayk Schörling"
          lines={[
            `${LEGAL_ADDRESS.street}, ${LEGAL_ADDRESS.zip} ${LEGAL_ADDRESS.city}`,
            KONTAKT_MAIL,
            BOOKING_MAIL,
          ]}
        />

        <h3>Schörling Naturheilpraxis</h3>
        <p>Bodywork, naturheilkundliche und körperorientierte Arbeit.</p>
        <PersonBlock
          name="Heilpraktiker Rayk Schörling"
          lines={[
            "Heilpraktikerin Katrin Schörling",
            ...PRAXIS_ADDRESSES.map((item) => `${item.street}, ${item.zip} ${item.city}`),
            `Telefon: ${LEGAL_PHONE}`,
            BOOKING_MAIL,
          ]}
        />

        <h3>Schörling Seminare</h3>
        <p>
          Familienaufstellungen, Seminare und Veranstaltungen. Einzelunternehmen. Onlinezahlung über
          Stripe.
        </p>
        <PersonBlock
          name="Katrin Schörling"
          lines={[
            `${LEGAL_ADDRESS.street}, ${LEGAL_ADDRESS.zip} ${LEGAL_ADDRESS.city}`,
            `Telefon: ${LEGAL_PHONE}`,
            BOOKING_MAIL,
          ]}
        />
      </section>

      <section>
        <h2>Heilpraktiker · berufsrechtliche Angaben</h2>
        <p>
          Die Berufsbezeichnung Heilpraktikerin / Heilpraktiker ist in Deutschland nach dem
          Heilpraktikergesetz (HeilprG) vom 17. Februar 1939 geschützt. Die Erlaubnis zur Ausübung
          der Heilkunde ohne Bestallung wurde erteilt:
        </p>
        <ul>
          <li>Katrin Schörling: 02.11.2006</li>
          <li>Rayk Schörling: 09.11.2006</li>
        </ul>
        <p>
          Zuständige Behörde: Gesundheitsamt Berlin, Bezirksamt Tempelhof-Schöneberg, Rathausstraße
          27, 12105 Berlin.
        </p>
        <p>
          Maßgeblich sind das Heilpraktikergesetz, die Erste Durchführungsverordnung dazu sowie die
          Berufsordnung und Ethikerklärung des Fachverbands Deutscher Heilpraktiker e.&nbsp;V.
        </p>
        <p>
          Aufsicht: Landesamt für Gesundheit und Soziales Berlin, Turmstraße 21, 10559 Berlin, und
          Gesundheitsamt Oranienburg, Havelstraße 29, 16515 Oranienburg.
        </p>
        <p>Berufshaftpflicht: SIGNAL IDUNA Allgemeine Versicherung AG, Dortmund.</p>
      </section>

      <section id="teilnahme">
        <h2>Buchung und Teilnahme</h2>
        <p>
          Eine Buchung oder Anmeldung ist verbindlich. Persönliche Termine werden bestätigt, sobald
          der Kalender den Zeitpunkt übernommen hat. Für Veranstaltungen gilt die gewählte
          Teilnahmeart; Onlinezahlung läuft über Stripe, Rechnung kommt separat.
        </p>
        <p>
          Stornierung persönlicher Termine über den Verwaltungslink, solange der Termin noch nicht
          begonnen hat, oder per Mail an {BOOKING_MAIL}. Für Veranstaltungen gilt die Absage per
          Mail. Erstattungen, soweit sie in Frage kommen, klären wir im Einzelfall.
        </p>
      </section>

      <section>
        <h2>Haftung</h2>
        <p>
          Die Inhalte dieser Seite werden mit Sorgfalt gepflegt. Für Richtigkeit, Vollständigkeit
          und Aktualität gilt die gesetzliche Haftung nach dem DDG.
        </p>
        <p>
          Externe Links werden beim Setzen geprüft. Für spätere Inhalte verlinkter Seiten sind deren
          Betreiber verantwortlich.
        </p>
      </section>

      <section>
        <h2>Streitschlichtung</h2>
        <p>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung bereit:{" "}
          <a href="https://ec.europa.eu/consumers/odr" rel="noreferrer" target="_blank">
            ec.europa.eu/consumers/odr
          </a>
          . Wir sind nicht verpflichtet und nicht bereit, an Streitbeilegungsverfahren vor einer
          Verbraucherschlichtungsstelle teilzunehmen.
        </p>
      </section>
    </LegalShell>
  );
}

export function DatenschutzPage() {
  return (
    <LegalShell
      title="Datenschutz"
      kicker="Datenschutzerklärung"
      heading="Wie wir mit deinen Angaben umgehen."
    >
      <p className="legal-page__lead">
        Nur was für die Buchung nötig ist. Keine Werbung, kein Tracking, kein Verkauf von Daten.
      </p>

      <section>
        <h2>Verantwortliche</h2>
        <p>
          Für den Betrieb dieser Buchungsseite ist Rayk Schörling verantwortlich. Für den Vertrag
          und die dazugehörigen Daten ist das Haus verantwortlich, über das du buchst:
        </p>
        <ul>
          <li>
            <strong>Coaching und Kennenlernen:</strong> Rayk Schörling
          </li>
          <li>
            <strong>Bodywork und Praxis:</strong> Rayk Schörling und Katrin Schörling, Schörling
            Naturheilpraxis
          </li>
          <li>
            <strong>Seminare und Aufstellungen:</strong> Katrin Schörling, Schörling Seminare
          </li>
        </ul>
        <PersonBlock
          name="Kontakt für Datenschutz auf dieser Seite"
          lines={[
            "Rayk Schörling",
            `${LEGAL_ADDRESS.street}, ${LEGAL_ADDRESS.zip} ${LEGAL_ADDRESS.city}`,
            KONTAKT_MAIL,
            BOOKING_MAIL,
          ]}
        />
      </section>

      <section>
        <h2>Welche Daten wir verarbeiten</h2>
        <p>Wenn du einen Termin oder eine Veranstaltung buchst:</p>
        <ul>
          <li>Name, E-Mail, optional Telefon und Anliegen</li>
          <li>gewähltes Angebot, Dauer, Ort, Zeitpunkt</li>
          <li>Zahlungsweg und Zahlungsstatus</li>
          <li>bei Online-Terminen die Google-Meet-Adresse</li>
        </ul>
        <p>
          Rechtsgrundlage ist Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;b DSGVO (Vertrag und vorvertragliche
          Schritte) und deine Einwilligung nach Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;a DSGVO, soweit du
          das Häkchen setzt.
        </p>
        <p>
          Bei Bodywork und Praxis können Angaben zum Anliegen Gesundheitsdaten sein. Die
          Verarbeitung erfolgt nur für die Behandlung, Art.&nbsp;9 Abs.&nbsp;2 lit.&nbsp;h DSGVO.
        </p>
      </section>

      <section>
        <h2>Wofür</h2>
        <ul>
          <li>Termin- und Veranstaltungsbuchung</li>
          <li>Kalendereinladung und Erinnerung</li>
          <li>Bestätigung, Rechnung, Zahlung</li>
          <li>Storno und Rückfragen</li>
        </ul>
        <p>Wir nutzen die Daten nicht für Newsletter, Werbung oder Profiling.</p>
      </section>

      <section>
        <h2>Wer uns dabei hilft</h2>
        <ul>
          <li>
            <strong>ALL-INKL.COM / Neue Medien Münnich,</strong> Hauptstraße 68, 02742 Friedersdorf
            – Hosting der Website
          </li>
          <li>
            <strong>Supabase</strong> – Datenbank und serverseitige Abläufe der Buchung
          </li>
          <li>
            <strong>Google Ireland Limited,</strong> Gordon House, Barrow Street, Dublin 4, Irland –
            Google Calendar und Google Meet für Terminorganisation und Online-Sitzungen
          </li>
          <li>
            <strong>Resend</strong> – Versand der Buchungsmails von
            booking@notify.raykschoerling.com
          </li>
          <li>
            <strong>Stripe Payments Europe Ltd.,</strong> Dublin – nur bei Onlinezahlung für
            Seminare und Aufstellungen. Coaching und Praxis laufen über Rechnung, nicht über Stripe.
          </li>
        </ul>
        <p>
          Soweit Anbieter Daten außerhalb der EU verarbeiten, geschieht das auf Grundlage der
          Standardvertragsklauseln der EU-Kommission.
        </p>
      </section>

      <section>
        <h2>Cookies und Protokolle</h2>
        <p>
          Diese Seite setzt keine Tracking-, Analyse- oder Marketing-Cookies und kein
          Consent-Banner, weil nichts Optionales geladen wird. Der Hoster speichert übliche
          Serverprotokolle (IP-Adresse, Zeitpunkt, aufgerufene Seite) zur Sicherheit und zum
          technischen Betrieb, Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;f DSGVO.
        </p>
      </section>

      <section>
        <h2>Speicherdauer</h2>
        <p>
          Buchungsdaten bleiben so lange, wie der Vertrag und gesetzliche Aufbewahrung es verlangen
          – für Rechnungen in der Regel zehn Jahre. Kalendereinträge und Mails löschen oder sperren
          wir, wenn sie nicht mehr nötig sind.
        </p>
      </section>

      <section>
        <h2>Deine Rechte</h2>
        <p>
          Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch.
          Einwilligungen kannst du jederzeit widerrufen. Schreib an {KONTAKT_MAIL} oder{" "}
          {BOOKING_MAIL}.
        </p>
        <p>
          Beschwerde: Berliner Beauftragte für Datenschutz und Informationsfreiheit, Alt-Moabit
          59–61, 10555 Berlin.
        </p>
      </section>
    </LegalShell>
  );
}

function LegalShell({
  title,
  kicker,
  heading,
  children,
}: {
  title: string;
  kicker: string;
  heading: string;
  children: ReactNode;
}) {
  const identity = publicSiteIdentity();

  useEffect(() => {
    document.title = `${title} · ${identity.brand}`;
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    });
  }, [identity.brand, title]);

  return (
    <div className="public-v5 legal-page">
      <header className="legal-page__header">
        <a className="legal-page__brand" href={identity.siteUrl}>
          {identity.brand}
        </a>
        <a className="legal-page__back" href="/">
          ← Übersicht
        </a>
      </header>
      <article className="legal-page__article">
        <p className="legal-page__kicker">{kicker}</p>
        <h1>{heading}</h1>
        {children}
      </article>
      <SiteFooter />
    </div>
  );
}

function PersonBlock({ name, lines }: { name: string; lines: string[] }) {
  return (
    <address className="legal-page__person">
      <strong>{name}</strong>
      {lines.map((line) =>
        line.includes("@") ? (
          <a key={line} href={`mailto:${line.replace(/^Buchung:\s*/, "")}`}>
            {line}
          </a>
        ) : line.startsWith("Telefon:") ? (
          <a key={line} href={`tel:${line.replace(/^Telefon:\s*/, "").replace(/\s+/g, "")}`}>
            {line}
          </a>
        ) : (
          <span key={line}>{line}</span>
        ),
      )}
    </address>
  );
}
